import { TemplatedReportsGeneratedReportDetailsRoutePath } from "#routes/reports/templated-reports/template/[templateName]/generated/[reportId]";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { formatQuestionAnswer } from "#src/components/Forms/FormSubmissionDetail";
import { useGetOneFormSubmission } from "#src/components/hooks/adapters/useFormSubmissions";
import { useGetOneUser } from "#src/components/hooks/adapters/useUsers";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useNavigate } from "#src/Routers/hooks";
import { linkToFormSubmissionDetail } from "#src/Routers/links";
import { linkToRecords } from "#src/routes/records/records";
import { displayValueWithUnknownDataType } from "#src/utils/display";
import { ExceptionUtils } from "#utils/exception";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  ButtonWithPopover,
  DateDataDisplay,
  KeyValueList,
  Pill,
  PillProps,
  Skeleton,
  TextDataDisplay,
  toast,
} from "@validereinc/common-components";
import {
  AssetType,
  ReportingGroupAdapter,
  ReportV3Adapter,
  Resources,
  WorkflowUserTaskAttachment,
  WorkflowUserTaskAttachmentFormSubmission,
  WorkflowUserTaskAttachmentRecords,
  WorkflowUserTaskAttachmentReport,
  WorkflowUserTaskAttachmentType,
  type FormSubmissionStatusType,
} from "@validereinc/domain";
import {
  DateFormats,
  downloadLink,
  ExportError,
  invariant,
  toExpandedObject,
  toTitleCaseString,
  yearMonthName,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useCallback, useMemo } from "react";
import { convertYearMonthsToISO, normalizeFilters } from "./helpers";
import styles from "./WorkflowTaskAttachment.module.scss";

const cx = classNames.bind(styles);

export const getAttachmentTitle = (attachment: WorkflowUserTaskAttachment) => {
  if (attachment.title) {
    return attachment.title;
  }
  if (attachment.type === WorkflowUserTaskAttachmentType.FORM_SUBMISSION) {
    return "Submission to Review";
  }
  if (attachment.type === WorkflowUserTaskAttachmentType.RECORD) {
    return "Required Record Filters";
  }
  return "Details";
};

export const FormSubmissionAttachmentHeader = ({
  attachment,
}: {
  attachment: WorkflowUserTaskAttachmentFormSubmission;
}) => {
  return (
    <RoutingLink to={linkToFormSubmissionDetail(attachment.form_submission_id)}>
      <Button
        iconPosition="left"
        icon="share"
      >
        View
      </Button>
    </RoutingLink>
  );
};

export const RecordAttachmentHeader = ({
  attachment,
}: {
  attachment: WorkflowUserTaskAttachmentRecords;
  taskId: string;
}) => {
  const navigate = useNavigate();
  const [equipmentRecordsStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["records", "list", "equipment"]
  );
  const [flowRecordStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["records", "list", "flows"]
  );
  const [facilityRecordStorageKey] = useStorageKey(
    ["filter-configuration"],
    ["records", "list", "facilities"]
  );
  const [calcsAndRecordsSharedStorageKey] = useStorageKey(
    ["view-configuration"],
    "calculations-records-shared"
  );

  const [currentSharedViewFilters, setSharedViewFilters] = useStickyState(
    {},
    calcsAndRecordsSharedStorageKey
  );
  const [, setEquipmentTableFilters] = useStickyState(
    {},
    equipmentRecordsStorageKey
  );
  const [, setFlowTableFilters] = useStickyState({}, flowRecordStorageKey);
  const [, setFacilityTableFilters] = useStickyState(
    {},
    facilityRecordStorageKey
  );

  const assetType = attachment.asset_type;
  const filter = useMemo(() => {
    const result = toExpandedObject(normalizeFilters(attachment.filter));
    convertYearMonthsToISO(result);
    return result;
  }, [attachment.filter]);

  const getTabSearchParams = () => {
    if (assetType === AssetType.EQUIPMENT) {
      return "";
    } else if (assetType === AssetType.FACILITY) {
      return "?tab=facilities";
    } else if (assetType === AssetType.FLOW) {
      return "?tab=flows";
    } else return "";
  };

  const onNavigate = () => {
    // IMPROVE: don't mutate the filter object directly, use immer
    const sharedFilters: Record<string, any> = {};
    if (filter.year_month) {
      sharedFilters.date_range = {
        from: filter.year_month.from,
        to: filter.year_month.to,
      };
      delete filter.year_month;
    }
    if (filter.reporting_group_id) {
      sharedFilters.reporting_group_id = filter.reporting_group_id;
      delete filter.reporting_group_id;
    }
    setSharedViewFilters({
      ...currentSharedViewFilters,
      ...sharedFilters,
    });

    if (assetType === AssetType.EQUIPMENT) {
      setEquipmentTableFilters(filter);
    } else if (assetType === AssetType.FACILITY) {
      setFacilityTableFilters(filter);
    } else if (assetType === AssetType.FLOW) {
      setFlowTableFilters(filter);
    }
    navigate({
      pathname: linkToRecords(),
      search: getTabSearchParams(),
    });
  };

  return (
    <div className={cx("accordion-actions")}>
      <Button
        iconPosition="left"
        icon="share"
        onClick={onNavigate}
      >
        View
      </Button>
    </div>
  );
};

const FormSubmissionAttachment = ({
  attachment,
}: {
  attachment: WorkflowUserTaskAttachmentFormSubmission;
}) => {
  const {
    measurementUnits,
    getPrecisionByType,
    isLoading: measurementsLoading,
  } = useMeasurementTypes();

  const formSubmissionQuery = useGetOneFormSubmission({
    id: attachment.form_submission_id,
  });
  const formSubmission = formSubmissionQuery?.data?.data;

  const submittedByUserQuery = useGetOneUser({
    id: formSubmission?.created_by,
  });

  const isLoading =
    measurementsLoading ||
    formSubmissionQuery.isLoading ||
    (submittedByUserQuery.isLoading && !!formSubmission?.created_by);
  const isFetching =
    formSubmissionQuery.isFetching ||
    (submittedByUserQuery.isFetching && !!formSubmission?.created_by);

  return (
    <Skeleton
      isLoading={isLoading}
      isBusy={isFetching}
    >
      <KeyValueList
        variant="shaded"
        color="neutral"
        data={[
          {
            title: "Form Submission",
            value: formSubmission?.form_schema ? (
              <RoutingLink
                to={linkToFormSubmissionDetail(attachment.form_submission_id)}
              >
                {formSubmission?.form_schema?.name} -
                {formSubmission?.id?.slice(0, 7)}
              </RoutingLink>
            ) : (
              "-"
            ),
          },
          {
            title: "Status",
            value: (
              <Pill
                variant={
                  formSubmission?.status
                    ? (
                        {
                          draft: "default",
                          pending: "info",
                          invalidated: "error",
                          validated: "success",
                          submitted: "success",
                        } satisfies Record<
                          FormSubmissionStatusType,
                          PillProps["variant"]
                        >
                      )[formSubmission?.status]
                    : "default"
                }
              >
                {toTitleCaseString(formSubmission?.status, { intent: "ui" })}
              </Pill>
            ),
          },
          ...(formSubmission?.form_schema?.question_preview ?? []).map(
            (preview) => {
              const previewQuestion =
                formSubmission?.form_schema?.config.questions?.[
                  preview.question_id
                ];
              if (!previewQuestion || !formSubmission?.form_schema) {
                return {
                  title: "-",
                  value: null,
                };
              }

              const previewAnswerIndex = formSubmission?.answers?.[
                preview.section_id
              ]?.findIndex(
                (section) => typeof section[preview.question_id] !== "undefined"
              );
              if (
                typeof previewAnswerIndex === "undefined" ||
                previewAnswerIndex === -1
              ) {
                return {
                  title: previewQuestion.prompt,
                  value: "-",
                };
              }

              const previewAnswer =
                formSubmission.answers[preview.section_id][previewAnswerIndex][
                  preview.question_id
                ];
              const formatAnswer = formatQuestionAnswer(getPrecisionByType);
              return {
                title: previewQuestion.prompt,
                value: formatAnswer(
                  previewQuestion,
                  preview.question_id,
                  preview.section_id,
                  previewAnswerIndex,
                  previewAnswer,
                  {
                    allQuestions: formSubmission.form_schema.config.questions,
                    allAnswers: formSubmission?.answers ?? {},
                    measurementUnits: measurementUnits,
                    formSchema: formSubmission.form_schema,
                  }
                ),
              };
            }
          ),
          {
            title: "Submitted By",
            value: submittedByUserQuery.data?.id ? (
              <UserDataDisplay
                sizeVariant="small"
                id={submittedByUserQuery.data.id}
                name={submittedByUserQuery.data?.name ?? "n/a"}
                alignment="right"
              />
            ) : (
              "-"
            ),
          },
          {
            title: "Date Submitted",
            value: (
              <DateDataDisplay
                value={submittedByUserQuery.data?.created_at}
                displayFormat={DateFormats.DATE_TIME}
                alignment="right"
              />
            ),
          },
        ]}
      />
    </Skeleton>
  );
};

export const ReportAttachmentKeyValueItem = ({
  attachment,
  taskId,
}: {
  attachment: WorkflowUserTaskAttachmentReport;
  taskId: string;
}) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [Resources.REPORT, attachment.report_id],
    queryFn: () => {
      if (!attachment.report_id) return;
      return ReportV3Adapter.getOne({
        id: attachment.report_id,
      });
    },
    enabled: !!attachment.report_id,
    staleTime: 3 * 60 * 1000,
  });

  const report = data?.data;
  const downloadReport = useCallback(() => {
    try {
      invariant(
        report?.s3_download_link,
        "Failed to download report: download link does not exist."
      );
      toast.push({
        intent: "success",
        description: "Successfully exported report.",
      });
      downloadLink(report.s3_download_link, report.name);
    } catch (error) {
      const normalizedError = new ExportError("Failed to export report", {
        cause: error,
        getExplanation: () => "Unable to export report. Try again.",
      });
      toast.push({
        intent: "error",
        description: normalizedError.getExplanation(),
      });
      ExceptionUtils.reportException(normalizedError, "error", {
        sourceComponent: "ReportAttachmentHeader",
      });
    }
  }, [report]);

  return {
    title: (
      <Skeleton
        className={cx("report-title-skeleton")}
        isLoading={isLoading}
        isBusy={isFetching}
      >
        <RoutingLink
          to={
            report?.templated_report_name && report?.latest_version.version
              ? TemplatedReportsGeneratedReportDetailsRoutePath.toLinkParts({
                  pathParams: {
                    reportId: report.id,
                    templateName: report.templated_report_name,
                  },
                  queryParams: {
                    version: report.latest_version.version.toString(),
                    taskId: taskId,
                  },
                })
              : ""
          }
        >
          {attachment.title ?? "-"}
        </RoutingLink>
      </Skeleton>
    ),
    value: (
      <ButtonWithPopover
        buttonProps={{
          icon: "arrow-square-out",
          disabled: !report?.s3_download_link,
          isLoading: isLoading,
          isBusy: isFetching,
          onClick: downloadReport,
          size: "x-small",
        }}
        label="Export"
      />
    ),
  };
};

export const ReportAttachment = ({
  taskId,
  attachments,
}: {
  taskId: string;
  attachments: WorkflowUserTaskAttachmentReport[];
}) => {
  return (
    <KeyValueList
      variant="shaded"
      color="neutral"
      className={cx("reports-accordion")}
      data={[
        ...attachments.map((attachment) =>
          ReportAttachmentKeyValueItem({
            attachment,
            taskId,
          })
        ),
      ]}
    />
  );
};

const RecordAttachment = ({
  attachment,
}: {
  attachment: WorkflowUserTaskAttachmentRecords;
}) => {
  const filter = useMemo(
    () => normalizeFilters(attachment.filter),
    [attachment.filter]
  );

  const { data: reportingGroup, isLoading: reportingGroupLoading } = useQuery(
    [Resources.REPORTING_GROUP, filter.reporting_group_id ?? ""],
    () =>
      ReportingGroupAdapter.getOne({
        id: filter.reporting_group_id ?? "",
      }),
    {
      enabled: !!filter.reporting_group_id,
    }
  );

  const formatYearMonths = (
    value: string | number | string[] | number[] | boolean | object | null
  ) => {
    let text = "-";
    if (value !== null && typeof value === "object") {
      if ("from" in value) {
        text = `${yearMonthName((value as { from: string; to: string }).from)} ${text}`;
      }
      if ("to" in value) {
        text = `${text} ${yearMonthName((value as { from: string; to: string }).to)}`;
      }
      return text;
    } else return text;
  };

  const getItem = useCallback(
    (
      key: string,
      value: string | number | string[] | number[] | boolean | object | null
    ) => {
      if (key === "reporting_group_id") {
        return {
          title: "Reporting Group",
          value: (
            <TextDataDisplay
              value={reportingGroup?.data?.name}
              isLoading={reportingGroupLoading}
              alignment="right"
            />
          ),
        };
      }
      if (key === "year_month") {
        return {
          title: "Period",
          value: (
            <TextDataDisplay
              value={formatYearMonths(value)}
              alignment="right"
            />
          ),
        };
      }
      return {
        title: toTitleCaseString(key.replaceAll(".", "_"), {
          intent: "ui",
        }).replace(" Id", ""),
        value: displayValueWithUnknownDataType(value, {
          alignment: "right",
        }),
      };
    },
    [reportingGroup, reportingGroupLoading]
  );

  const items = useMemo(
    () =>
      [
        {
          title: "Asset Type",
          value: (
            <TextDataDisplay
              value={toTitleCaseString(attachment.asset_type, { intent: "ui" })}
              alignment="right"
            />
          ),
        },
        ...Object.entries(filter).map(([key, value]) => getItem(key, value)),
      ].sort((a, b) => a.title.localeCompare(b.title)),
    [filter, attachment.asset_type, getItem]
  );

  return (
    <KeyValueList
      variant="shaded"
      color="neutral"
      data={items}
    />
  );
};

export const WorkflowTaskAttachment = ({
  attachment,
}: {
  attachment: WorkflowUserTaskAttachment;
}) => {
  if (attachment.type === WorkflowUserTaskAttachmentType.FORM_SUBMISSION) {
    return <FormSubmissionAttachment attachment={attachment} />;
  }
  if (attachment.type === WorkflowUserTaskAttachmentType.RECORD) {
    return <RecordAttachment attachment={attachment} />;
  }
  return null;
};
